import moment, {Moment} from "moment";
import gql from "graphql-tag";
import {ScheduledFlightFragment} from "../../../Queries/Scheduling";
import {useQuery} from "react-apollo";
import {FlightNode, QueryGetScheduledFlightsArgs} from "../../../__generated__/graphql/types";
import {momentOrNull} from "../../../common/util";

const QUERY = gql`
    query UseGenerateFlightDesig_GetFlights(
        $tpID: ID!
        $customerID: ID!
        $startDate: AWSDate!
        $endDate: AWSDate!
    ){
        getScheduledFlights(
            tpID: $tpID
            customerID: $customerID
            startDate: $startDate
            endDate: $endDate
        ){
            ...ScheduledFlightFragment
        }
    }
    ${ScheduledFlightFragment}
`

export interface UseGenerateFlightDesigArgs {
    scheduledFlightDate: string | Moment,
    contractName: string,
    tpID: string,
    customerID: string
}

type Data = {
    getScheduledFlights: FlightNode[]
}

type Variables = QueryGetScheduledFlightsArgs

/**
 * Generates a flight desig from a contract name. If there already exists a flight within the same scheduled
 * flight date with the same desig, a number will be appended to the end of the name.
 * Example 1:
 *
 * contractName: TALOS 20
 *
 * Existing flights:
 * - TALOS 20
 * - TALOS 20 #1
 *
 * returns TALOS 20 #2
 *
 * Example 2:
 *
 * contractName: MURPHY 10
 *
 * No existing flights
 *
 * returns MURPHY 10 #1
 *
 * @param args
 */
export function useGenerateFlightDesig(args: UseGenerateFlightDesigArgs){

    const { scheduledFlightDate, contractName, tpID, customerID } = args;

    const date = momentOrNull(scheduledFlightDate);

    const result = useQuery<Data, Variables>(QUERY, {
        variables: {
            tpID: tpID,
            customerID: customerID,
            startDate: date.startOf('day').format('YYYY-MM-DD'),
            endDate: date.endOf('day').format('YYYY-MM-DD')
        },
        skip: !date
    });

    const desig = createFlightDesig(contractName, result?.data?.getScheduledFlights);

    return {
        loading: result.loading,
        desig: result.loading && !result.error ? null : desig,
        error: result.error
    }
}

export function createFlightDesig(contractName: string, flights: FlightNode[]) {
    // Return null if contractName is null or undefined
    if (!contractName) {
        return null;
    }

    // If flights is null or empty, return the first designation
    if (!flights || flights.length === 0) {
        return `${contractName} #1`;
    }

    // Regex to match designations in the format "contractName #<number>"
    const regex = new RegExp(`^${contractName} #(\\d+)$`);

    // Extract numbers from matching designations
    const matchingNumbers = flights
        .map(flight => {
            const match = flight.desig?.match(regex);
            return match ? parseInt(match[1], 10) : null;
        })
        .filter(num => num !== null) as number[];

    // Determine the next number
    const nextNumber = matchingNumbers.length > 0 ? Math.max(...matchingNumbers) + 1 : 1;

    // Construct and return the new designation
    return `${contractName} #${nextNumber}`;
}
